// ─────────────────────────────────────────────────────────────────────
// «LOG OUT» MEANS EVERY CUSTOMER SESSION THIS BROWSER HOLDS (#752).
//
// ── WHAT WAS WRONG ─────────────────────────────────────────────────
//
// Every sign-out control in the app called `clubSession.signOut()`, which ends
// the ACCOUNT session and clears `sps_session`. `memberSession.signOut()` was
// never called from anywhere — the function existed, posted to
// `/auth/member/logout`, and nothing in the browser ever asked it to.
//
// So a join-code member who pressed «logg ut» had their screen cleared and
// their session left alive: the cookie survived, the row survived, and the
// next reload ran `memberSession.restore()` and signed them straight back in.
// On a shared machine that is somebody else's session, not a stale screen.
//
// It is the same shape as #620 — *«a logout that clears the screen and nothing
// else is the worst of the three outcomes»* — which is the bug
// `memberSession.signOut` was written to fix, and then nothing called it.
//
// ── WHY A LIST RATHER THAN TWO CALLS AT EACH SITE ──────────────────
//
// Because the defect was a MISSING call at three sites, and two calls at three
// sites is the same defect waiting for a fourth site. #752 states the rule as
// «logout clears every customer cookie, **not the one the UI believes it is
// using**» — so there is one place that knows what «every» means, and the
// controls ask for all of them without naming any.
//
// A new customer credential family is added HERE, and every sign-out control
// gets it for free. That is the whole point.
// ─────────────────────────────────────────────────────────────────────

/** The part of a session this needs: a way to end it. */
export interface EndableSession {
  signOut(): Promise<void> | void;
}

/**
 * End them all, and let none of them prevent the others.
 *
 * `allSettled`, not `all`: these are independent network calls to independent
 * endpoints, and a rejection from one must not leave another session alive —
 * which is exactly the failure mode being fixed. Each session already swallows
 * its own transport errors and clears its local state first, so a rejection
 * here means something unexpected rather than «the server was unreachable».
 *
 * The result is deliberately `void`: there is no partial success worth
 * reporting to a caller whose next act is to show the signed-out screen, and a
 * boolean would invite somebody to branch on it and leave a session alive.
 */
export async function endCustomerSessions(sessions: readonly EndableSession[]): Promise<void> {
  // ── `async` ON THE MAPPER IS LOAD-BEARING ──────────────────────────
  //
  // `map((s) => s.signOut())` looks equivalent and is not: a signOut that
  // throws SYNCHRONOUSLY escapes the map before `allSettled` ever receives an
  // array, so the sessions after it are never even called — the exact failure
  // this function exists to prevent, reintroduced one line below the comment
  // promising it would not be. The first version did that, and its own test
  // caught it.
  //
  // An `async` arrow turns a synchronous throw into a rejection, which is what
  // `allSettled` is for.
  await Promise.allSettled(sessions.map(async (session) => session.signOut()));
}
